/**
 * String Utility Functions
 *
 * Pure functions that replace the String.prototype extensions from prototype.js.
 * These provide the same functionality without polluting the global prototype.
 *
 * Migration mapping:
 * - String.prototype.beszur -> insertAt
 * - String.prototype.capitalize -> capitalize
 * - String.prototype.extraTrim -> extraTrim
 * - String.prototype.ltrim -> ltrim
 * - String.prototype.rtrim -> rtrim
 *
 * Note: String.prototype.trim is native since ES5, no replacement needed.
 * Note: Array.prototype.inTomb -> use native Array.prototype.includes()
 */

/**
 * Insert a string at a specific position.
 *
 * @param str - The original string
 * @param what - The string to insert
 * @param pos - The position to insert at
 * @returns The new string with the insertion
 *
 * @example
 * insertAt('hello', ' world', 5) // 'hello world'
 * insertAt('abc', 'X', 1) // 'aXbc'
 */
export function insertAt(str: string, what: string, pos: number): string {
    return str.substring(0, pos) + what + str.slice(pos);
}

/**
 * Capitalize the first letter of a string.
 *
 * @param str - The string to capitalize
 * @returns The string with the first letter capitalized
 *
 * @example
 * capitalize('hello') // 'Hello'
 * capitalize('HELLO') // 'HELLO'
 */
export function capitalize(str: string): string {
    if (str.length === 0) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Remove all newlines and collapse multiple whitespace into single spaces.
 * Also trims leading and trailing whitespace.
 *
 * @param str - The string to clean
 * @returns The cleaned string
 *
 * @example
 * extraTrim('  hello\n  world  ') // 'hello world'
 * extraTrim('a   b   c') // 'a b c'
 */
export function extraTrim(str: string): string {
    // Remove all newlines (CR, LF, CRLF)
    let result = str.replace(/(\r\n|\n|\r)/gm, ' ');
    // Collapse multiple whitespace into single space
    result = result.replace(/\s+/g, ' ');
    // Trim leading and trailing whitespace
    return result.trim();
}

/**
 * Remove leading whitespace from a string.
 *
 * @param str - The string to trim
 * @returns The string with leading whitespace removed
 *
 * @example
 * ltrim('  hello') // 'hello'
 * ltrim('hello  ') // 'hello  '
 */
export function ltrim(str: string): string {
    return str.replace(/^\s+/g, '');
}

/**
 * Remove trailing whitespace from a string.
 *
 * @param str - The string to trim
 * @returns The string with trailing whitespace removed
 *
 * @example
 * rtrim('hello  ') // 'hello'
 * rtrim('  hello') // '  hello'
 */
export function rtrim(str: string): string {
    return str.replace(/\s+$/g, '');
}

/**
 * Replace Hungarian quote characters for proper typography.
 * Converts ASCII quotes to Hungarian opening/closing quotes.
 *
 * @param str - The string to process
 * @param position - Current cursor position to determine quote direction
 * @returns Object with the character to insert
 *
 * @example
 * // For opening quote (no previous opening quote or after closing)
 * getHungarianQuote('"test', 0) // { char: '„' }
 * // For closing quote (after opening quote)
 * getHungarianQuote('„test', 5) // { char: '"' }
 */
export function getHungarianQuote(
    str: string,
    position: number,
): { char: string } {
    const beforeCursor = str.substring(0, position);
    const lastOpeningQuote = beforeCursor.lastIndexOf('„');
    const lastClosingQuote = beforeCursor.lastIndexOf('"');

    // If there's an opening quote that hasn't been closed, use closing quote
    if (lastOpeningQuote > lastClosingQuote) {
        return { char: '"' };
    }

    // Otherwise use opening quote
    return { char: '„' };
}

/**
 * Replace ASCII apostrophe with Hungarian apostrophe.
 *
 * @returns The Hungarian apostrophe character
 */
export function getHungarianApostrophe(): string {
    return '\u2019'; // Right single quotation mark (')
}
